import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  Factory, Package, Clock, AlertTriangle, ChevronDown, ChevronUp,
  Calendar, Users, CheckCircle, Circle, PlayCircle, TrendingUp,
  Layers, ArrowRight, ArrowLeft, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Producao() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState([]);
  const [categoriaExpandida, setCategoriaExpandida] = useState(null);
  const [categoriaDetalhes, setCategoriaDetalhes] = useState(null);
  const [loading, setLoading] = useState(true);

  const carregarDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/producao/dashboard`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
    setLoading(false);
  }, []);

  const carregarDetalhesCategoria = useCallback(async (categoriaId) => {
    try {
      const res = await fetch(`${API}/api/producao/categoria/${categoriaId}`);
      const data = await res.json();
      setCategoriaDetalhes(data);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
    }
  }, []);

  useEffect(() => {
    carregarDashboard();
    // Atualizar a cada 30 segundos
    const interval = setInterval(carregarDashboard, 30000);
    return () => clearInterval(interval);
  }, [carregarDashboard]);

  const expandirCategoria = async (categoriaId) => {
    if (categoriaExpandida === categoriaId) {
      setCategoriaExpandida(null);
      setCategoriaDetalhes(null);
    } else {
      setCategoriaExpandida(categoriaId);
      await carregarDetalhesCategoria(categoriaId);
    }
  };

  const getCorPrioridade = (prioridade) => {
    switch (prioridade) {
      case 'urgente': return 'bg-red-500 text-white';
      case 'alta': return 'bg-orange-500 text-white';
      case 'normal': return 'bg-blue-500 text-white';
      default: return 'bg-stone-400 text-white';
    }
  };

  const getCorProgresso = (progresso) => {
    if (progresso >= 100) return 'bg-green-500';
    if (progresso >= 75) return 'bg-emerald-500';
    if (progresso >= 50) return 'bg-amber-500';
    if (progresso >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getCorCategoria = (codigo) => {
    const cores = {
      'PRE': 'from-indigo-500 to-indigo-600',
      'INT': 'from-blue-500 to-blue-600',
      'ACA': 'from-emerald-500 to-emerald-600',
      'EMB': 'from-purple-500 to-purple-600'
    };
    return cores[codigo] || 'from-stone-500 to-stone-600';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  // Calcular totais gerais
  const totais = dashboard.reduce((acc, cat) => ({
    ofs: acc.ofs + cat.total_ofs,
    lotes: acc.lotes + cat.total_lotes,
    quantidade: acc.quantidade + cat.total_quantidade,
    concluidos: acc.concluidos + (cat.lotes_concluidos || 0)
  }), { ofs: 0, lotes: 0, quantidade: 0, concluidos: 0 });

  const progressoGeral = totais.quantidade > 0 && dashboard.length > 0
    ? Math.round(
        dashboard.reduce((acc, cat) => acc + cat.total_quantidade * (cat.progresso_geral || 0), 0) / totais.quantidade
      )
    : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-stone-600 hover:bg-stone-100">
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Factory className="h-6 w-6 text-navy" />
              Produção
            </h1>
            <p className="text-stone-500 mt-1">Visão geral da produção por categoria</p>
          </div>
        </div>
        <Button variant="outline" onClick={carregarDashboard} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <TrendingUp className="h-4 w-4 mr-2" />
          )}
          Atualizar
        </Button>
      </div>

      {/* Resumo Geral */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-gradient-to-br from-navy to-navy/80 text-white">
          <p className="text-xs text-white/70 uppercase">OFs em Curso</p>
          <p className="text-3xl font-bold">{totais.ofs}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <p className="text-xs text-white/70 uppercase">Total Lotes</p>
          <p className="text-3xl font-bold">{totais.lotes}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <p className="text-xs text-white/70 uppercase">Lotes Concluídos</p>
          <p className="text-3xl font-bold">{totais.concluidos.toLocaleString()}</p>
          <p className="text-xs text-white/70">de {totais.quantidade.toLocaleString()} peças</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <p className="text-xs text-white/70 uppercase">Progresso Geral</p>
          <p className="text-3xl font-bold">{progressoGeral}%</p>
          <div className="mt-2 h-2 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all"
              style={{ width: `${progressoGeral}%` }}
            />
          </div>
        </Card>
      </div>

      {/* Dashboard por Categoria */}
      <div className="space-y-4">
        {dashboard.map((cat) => (
          <Card 
            key={cat.categoria_id} 
            className={`overflow-hidden transition-all ${
              categoriaExpandida === cat.categoria_id ? 'ring-2 ring-navy shadow-lg' : ''
            }`}
          >
            {/* Header da Categoria */}
            <div 
              className={`p-4 cursor-pointer bg-gradient-to-r ${getCorCategoria(cat.categoria_codigo)} text-white`}
              onClick={() => expandirCategoria(cat.categoria_id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 rounded-lg p-2">
                    <Layers className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <span className="bg-white/20 px-2 py-0.5 rounded text-sm font-mono">
                        {cat.categoria_codigo}
                      </span>
                      {cat.categoria_nome}
                    </h2>
                    <p className="text-white/80 text-sm">
                      {cat.total_ofs} OFs • {cat.total_lotes} Lotes • {cat.total_quantidade.toLocaleString()} peças
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-2xl font-bold">{Math.min(cat.progresso_geral || 0, 100).toFixed(1)}%</p>
                    <p className="text-xs text-white/70">{cat.total_quantidade.toLocaleString()} peças</p>
                  </div>
                  <div className="w-24 h-3 bg-white/30 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-white transition-all"
                      style={{ width: `${Math.min(cat.progresso_geral || 0, 100)}%` }}
                    />
                  </div>
                  {categoriaExpandida === cat.categoria_id ? (
                    <ChevronUp className="h-6 w-6" />
                  ) : (
                    <ChevronDown className="h-6 w-6" />
                  )}
                </div>
              </div>
            </div>

            {/* Conteúdo Expandido */}
            {categoriaExpandida === cat.categoria_id && categoriaDetalhes && (
              <div className="p-4 bg-stone-50">
                {categoriaDetalhes.ofs.length === 0 ? (
                  <div className="text-center py-8 text-stone-500">
                    <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhuma OF em produção nesta categoria</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {categoriaDetalhes.ofs.map((of) => (
                      <Card key={of.of_id} className="p-4 bg-white">
                        {/* Header da OF */}
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-bold text-lg">{of.numero_of}</h3>
                              {of.prioridade && of.prioridade !== 'normal' && of.prioridade !== 'baixa' && (
                                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${getCorPrioridade(of.prioridade)}`}>
                                  {of.prioridade}
                                </span>
                              )}
                              <span className="text-xs px-2 py-0.5 rounded bg-stone-100 text-stone-600 font-medium">
                                {of.total_lotes} {of.total_lotes === 1 ? 'lote' : 'lotes'} · {of.total_quantidade} peças
                              </span>
                            </div>
                            <p className="text-stone-500 text-sm">{[of.cliente, of.marca, of.modelo].filter(Boolean).join(' · ') || '—'}</p>
                          </div>
                        </div>

                        {/* Lotes — cada um com a sua barra e progresso */}
                        <div className="space-y-3">
                          {of.lotes.map((lote) => (
                            <div 
                              key={lote.id}
                              className="border rounded-xl p-4 bg-stone-50"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-bold text-navy text-base">
                                    {lote.codigo_lote}
                                  </span>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-bold">
                                    {lote.quantidade} peças
                                  </span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                    lote.status === 'aprovado' ? 'bg-green-100 text-green-700' :
                                    lote.status === 'pendente' ? 'bg-amber-100 text-amber-700' :
                                    'bg-stone-100 text-stone-700'
                                  }`}>
                                    {lote.status}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span className="text-lg font-bold">{lote.progresso_tarefas}%</span>
                                  <span className="text-xs text-stone-500 ml-1.5">
                                    ({lote.tarefas_concluidas}/{lote.total_tarefas} ops)
                                  </span>
                                </div>
                              </div>

                              {/* Barra de progresso do lote */}
                              <div className="h-2 bg-stone-200 rounded-full overflow-hidden mb-3">
                                <div 
                                  className={`h-full ${getCorProgresso(lote.progresso_tarefas)} transition-all`}
                                  style={{ width: `${Math.min(lote.progresso_tarefas || 0, 100)}%` }}
                                />
                              </div>

                              {/* Operações do Lote */}
                              <div className="flex flex-wrap gap-1.5">
                                {lote.tarefas.map((tarefa, idx) => (
                                  <div
                                    key={tarefa.operacao_id}
                                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg ${
                                      tarefa.status === 'concluida' ? 'bg-green-100 text-green-700' :
                                      tarefa.status === 'em_curso' ? 'bg-blue-100 text-blue-700' :
                                      'bg-stone-200 text-stone-600'
                                    }`}
                                    title={`${tarefa.codigo || ''} - ${tarefa.operacao_nome}: ${tarefa.quantidade_produzida}/${tarefa.quantidade_total} (${tarefa.progresso}%) — Peso: ${tarefa.peso_percentagem || 0}% do lote`}
                                  >
                                    {tarefa.status === 'concluida' ? (
                                      <CheckCircle className="h-3 w-3" />
                                    ) : tarefa.status === 'em_curso' ? (
                                      <PlayCircle className="h-3 w-3" />
                                    ) : (
                                      <Circle className="h-3 w-3" />
                                    )}
                                    <span className="font-mono font-semibold">{tarefa.codigo || tarefa.operacao_nome?.substring(0, 5)}</span>
                                    <span className="font-semibold">{tarefa.progresso || 0}%</span>
                                    <span className="text-[10px] opacity-60 font-medium">({tarefa.peso_percentagem || 0}%)</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Footer da OF */}
                        {of.data_entrega && (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t text-sm text-stone-500">
                            <Calendar className="h-4 w-4" />
                            Entrega: {new Date(of.data_entrega).toLocaleDateString('pt-PT')}
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}

        {dashboard.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            <Factory className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold">Nenhuma produção em curso</h3>
            <p>As OFs em produção aparecerão aqui organizadas por categoria</p>
          </div>
        )}
      </div>
    </div>
  );
}
